import { type ChangeEvent, type DragEvent, type FC, type ReactNode, useCallback, useRef } from "react";

import { Card, IconButton, Spacing, Tag } from "@snapchat/snap-design-system";
import { GrabHandleIcon, PencilIcon, TrashIcon, UploadIcon } from "@snapchat/snap-design-system-icons";
import { type Identifier } from "dnd-core";
import { useDrag, useDrop } from "react-dnd";

import { Flexbox } from "../../shared/components";
import { type LensPreviewMediaItem, getItemId } from "../../shared/state";
import { ItemTypes, type PreviewAspectRatio, Scale } from "../models";

import {
    dndCardWrapperDraggingStyle,
    dndCardWrapperNormalStyle,
    emptyCardContainerStyle,
    emptyCardContentWrapperStyle,
    filledCardContainerStyle,
    hiddenFileInputStyle,
    iconButtonCss,
    primaryTagCss,
    uploadButtonStyle,
} from "./DnDPreviewCard.styles";
import { PreviewViewerWithCTA } from "./PreviewViewerWithCTA";

interface DnDPreviewCardProps {
    preview: LensPreviewMediaItem;
    index: number;
    onMovePreview: (dragIndex: number, hoverIndex: number) => void;
    onFileUpload: (file: File, index: number) => Promise<void>;
    onRemovePreview: (idx: number) => void;
    onEditPreview: (preview: LensPreviewMediaItem, index: number) => void;
    aspectRatio: PreviewAspectRatio;
    acceptFormats: string;
    isEmpty: boolean;
    uploadCta: ReactNode;
}

interface DragItem {
    id: string;
    index: number;
    type: string;
}

export const DnDPreviewCard: FC<DnDPreviewCardProps> = ({
    preview,
    index,
    onMovePreview,
    onFileUpload,
    onRemovePreview,
    onEditPreview,
    aspectRatio,
    acceptFormats,
    isEmpty,
    uploadCta,
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
        accept: ItemTypes.PREVIEW_CARD,
        canDrop: () => !isEmpty, // Can only drop onto an existing preview for reordering
        collect: (monitor) => {
            return {
                handlerId: monitor.getHandlerId(),
            };
        },
        hover: (item: DragItem, monitor) => {
            if (!ref.current || isEmpty) {
                // Do not allow hovering over empty slots for reorder
                return;
            }
            const dragIndex = item.index;
            const hoverIndex = index;

            if (dragIndex === hoverIndex) {
                return;
            }

            const hoverBoundingRect = ref.current.getBoundingClientRect();
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            const clientOffset = monitor.getClientOffset();
            // If clientOffset is null, then nothing is being dragged
            if (!clientOffset) return;
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;

            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }

            onMovePreview(dragIndex, hoverIndex);
            // Note: we're mutating the item monitor information here!
            // Generally it's better to avoid mutations,
            // but it's good here for the sake of performance
            // to avoid expensive index searches.
            item.index = hoverIndex;
        },
    });

    const [{ isDragging }, drag] = useDrag({
        type: ItemTypes.PREVIEW_CARD,
        item: () => ({ id: getItemId(preview), index, type: ItemTypes.PREVIEW_CARD }), // `id` must exist if preview exists
        canDrag: () => !isEmpty, // Can only drag if there's a preview
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    // Drag and drop are connector functions. They link a DOM node to the
    // behaviours registered in the parameters passed to the useDrag and
    // useDrop hooks. They do not mutate ref. Here, we are telling
    // react-dnd that the DOM node this ref points to is a potential drop
    // target and a draggable item
    drag(drop(ref));

    const handleUploadClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleFileChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];
            if (file) {
                void onFileUpload(file, index);
            }
            if (fileInputRef.current) {
                fileInputRef.current.value = ""; // Reset input
            }
        },
        [index, onFileUpload],
    );

    const handleDrop = useCallback(
        (event: DragEvent<HTMLButtonElement>) => {
            event.preventDefault();
            const file = event.dataTransfer.files[0] ?? null;
            if (file) {
                void onFileUpload(file, index);
            }
        },
        [onFileUpload, index],
    );

    const preventDefault = useCallback((event: DragEvent<HTMLButtonElement>) => {
        event.preventDefault();
    }, []);

    const handleRemove = useCallback(() => {
        onRemovePreview(index);
    }, [index, onRemovePreview]);

    const handleEditClick = useCallback(() => {
        onEditPreview(preview, index);
    }, [onEditPreview, preview, index]);

    return (
        <div
            ref={ref}
            className={isDragging ? dndCardWrapperDraggingStyle : dndCardWrapperNormalStyle}
            data-handler-id={handlerId}
        >
            {isEmpty ? (
                <Card className={emptyCardContainerStyle} padBody={false}>
                    <div className={emptyCardContentWrapperStyle}>
                        <button
                            onClick={handleUploadClick}
                            className={uploadButtonStyle}
                            onDrop={handleDrop}
                            onDragOver={preventDefault}
                            onDragEnter={preventDefault}
                            onDragLeave={preventDefault}
                        >
                            <UploadIcon />
                            {uploadCta}
                        </button>
                    </div>
                </Card>
            ) : (
                <Card className={filledCardContainerStyle} padBody={false}>
                    <Flexbox justifyContent="space-between">
                        <Flexbox justifyContent="center" alignItems="center" gap={`${Spacing.SM}px`}>
                            <GrabHandleIcon />
                            <PreviewViewerWithCTA
                                key={getItemId(preview) + `-${index}`}
                                scale={Scale.Percent20}
                                isVideo={preview.isVideo}
                                src={preview.url}
                                aspectRatio={aspectRatio}
                                isEmpty={isEmpty}
                            />
                        </Flexbox>
                        <Flexbox gap={`${Spacing.SM}px`} alignItems="center" justifyContent="center">
                            {index === 0 && <Tag className={primaryTagCss}>Primary</Tag>}
                            <IconButton icon={<TrashIcon />} onClick={handleRemove} className={iconButtonCss} />
                            <IconButton icon={<PencilIcon />} onClick={handleEditClick} className={iconButtonCss} />
                        </Flexbox>
                    </Flexbox>
                </Card>
            )}
            <input
                type="file"
                ref={fileInputRef}
                className={hiddenFileInputStyle}
                onChange={handleFileChange}
                accept={acceptFormats}
            />
        </div>
    );
};
